/**
 * Canonical labour costing rules shared by the web app and the Worker.
 *
 * New rate records are daily rates. A worker-day is priced as:
 *   count x dailyRate x (workedMinutes / (60 x standardHours))
 *
 * Historical rows are explicitly marked HOURLY and keep the legacy formula:
 *   count x hours x hourlyRate
 */

export type RateBasis = "DAILY" | "HOURLY";

export const DEFAULT_STANDARD_WORK_DAY_HOURS = 8;

export function round2(value: number): number {
  return Math.round(value * 100) / 100;
}

export function normalizeStandardWorkDayHours(value: unknown): number {
  const hours = Number(value);
  return Number.isFinite(hours) && hours > 0 ? hours : DEFAULT_STANDARD_WORK_DAY_HOURS;
}

export function normalizeRateBasis(value: unknown): RateBasis {
  return String(value ?? "").trim().toUpperCase() === "HOURLY" ? "HOURLY" : "DAILY";
}

export function rateUnitLabel(rateBasis: RateBasis | string | null | undefined): string {
  return normalizeRateBasis(rateBasis) === "HOURLY" ? "hr" : "day";
}

export function calculateItemTotal(
  headcount: number,
  hours: number,
  rate: number,
  rateBasis: RateBasis | string = "DAILY",
  standardHours = DEFAULT_STANDARD_WORK_DAY_HOURS,
): number {
  if (normalizeRateBasis(rateBasis) === "HOURLY") {
    return round2(headcount * hours * rate);
  }
  const dayHours = normalizeStandardWorkDayHours(standardHours);
  return round2((headcount * rate * hours) / dayHours);
}

export function calculateWorkerTotal(
  count: number,
  minutes: number,
  rate: number,
  rateBasis: RateBasis | string = "DAILY",
  standardHours = DEFAULT_STANDARD_WORK_DAY_HOURS,
): number | null {
  if (!Number.isFinite(minutes) || minutes <= 0) return null;
  if (normalizeRateBasis(rateBasis) === "HOURLY") {
    return round2((count * minutes * rate) / 60);
  }
  const dayHours = normalizeStandardWorkDayHours(standardHours);
  return round2((count * rate * minutes) / (60 * dayHours));
}
